import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  User,
  Home,
  FileText,
  Calendar,
  Clock,
  Mail,
  Phone,
  MapPin,
  Building2,
  PoundSterling,
  Eye,
  MessageSquare,
  CheckCircle,
  XCircle,
  Loader2,
  Plus,
  ExternalLink,
} from "lucide-react";
import { API } from "@/App";

export default function ClientDashboardPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    loadClientData();
  }, [clientId]);

  const loadClientData = async () => {
    try {
      const res = await axios.get(`${API}/contacts/${clientId}/full`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (error) {
      console.error("Error loading client:", error);
      toast.error("Failed to load client data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-4xl mx-auto text-center py-20">
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Client Not Found</h2>
          <p className="text-slate-500 mb-4">The client you're looking for doesn't exist.</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  const { client, properties, valuations, documents, viewings, audit_trail } = data;

  const getStatusColor = (status) => {
    const colors = {
      active: "bg-green-100 text-green-700",
      on_market: "bg-blue-100 text-blue-700",
      under_offer: "bg-amber-100 text-amber-700",
      sold: "bg-purple-100 text-purple-700",
      let: "bg-emerald-100 text-emerald-700",
      withdrawn: "bg-slate-100 text-slate-700",
      draft: "bg-gray-100 text-gray-700",
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-purple-600" />
                  </div>
                  {client.full_name || client.name || "Unknown Client"}
                </h1>
                <div className="flex items-center gap-4 mt-1 text-slate-500">
                  {client.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {client.email}
                    </span>
                  )}
                  {(client.mobile || client.phone) && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {client.mobile || client.phone}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button
              onClick={() => navigate(`/new-instruction?client=${clientId}`)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Property
            </Button>
          </div>

          {/* Role Badges */}
          {client.roles && client.roles.length > 0 && (
            <div className="flex gap-2 mt-3 ml-14">
              {client.roles.map((role) => (
                <Badge key={role} variant="secondary">
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto p-6">
        <Tabs defaultValue="properties" className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="properties" className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              Properties ({properties?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Documents ({documents?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="viewings" className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Viewings ({viewings?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Audit Trail
            </TabsTrigger>
          </TabsList>

          {/* Properties Tab */}
          <TabsContent value="properties">
            <div className="grid gap-4">
              {properties && properties.length > 0 ? (
                properties.map((prop, idx) => (
                  <Card key={idx} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Building2 className="w-5 h-5 text-slate-400" />
                            <h3 className="font-semibold text-slate-900">
                              {prop.address || prop.full_address || prop.address_line1 || "Property"}
                            </h3>
                            <Badge className={getStatusColor(prop.status || prop.current_stage)}>
                              {(prop.status || prop.current_stage || "unknown").replace(/_/g, " ")}
                            </Badge>
                            <Badge variant="outline">
                              {prop.source === "sales_cases" ? "Sales" : 
                               prop.source === "lettings_properties" ? "Lettings" : 
                               prop.source || "Property"}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-4 gap-4 text-sm text-slate-600">
                            {prop.bedrooms && (
                              <div>
                                <span className="text-slate-400">Beds:</span> {prop.bedrooms}
                              </div>
                            )}
                            {prop.property_type && (
                              <div>
                                <span className="text-slate-400">Type:</span>{" "}
                                {prop.property_type.replace(/_/g, " ")}
                              </div>
                            )}
                            {(prop.asking_price || prop.price) && (
                              <div>
                                <span className="text-slate-400">Price:</span> £
                                {(prop.asking_price || prop.price).toLocaleString()}
                              </div>
                            )}
                            {prop.monthly_rent && (
                              <div>
                                <span className="text-slate-400">Rent:</span> £
                                {prop.monthly_rent.toLocaleString()} pcm
                              </div>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (prop.case_id) navigate(`/sales/case/${prop.case_id}`);
                            else if (prop.property_id) navigate(`/lettings/property/${prop.property_id}`);
                            else if (prop.listing_id) navigate(`/sales/listing/${prop.listing_id}`);
                          }}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Home className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="font-medium text-slate-900 mb-1">No Properties</h3>
                    <p className="text-slate-500 mb-4">This client has no properties yet.</p>
                    <Button
                      onClick={() => navigate(`/new-instruction?client=${clientId}`)}
                      variant="outline"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Property
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Valuations Section */}
            {valuations && valuations.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-slate-900 mb-3">Valuations</h3>
                <div className="grid gap-3">
                  {valuations.map((val, idx) => (
                    <Card key={idx}>
                      <CardContent className="p-4 flex items-center justify-between">
                        <div>
                          <div className="font-medium">
                            {val.property?.address_line1}, {val.property?.postcode}
                          </div>
                          <div className="text-sm text-slate-500">
                            {val.valuation_type} • {val.status}
                            {val.recommended_price && ` • £${val.recommended_price.toLocaleString()}`}
                          </div>
                        </div>
                        <Badge className={getStatusColor(val.status)}>{val.status}</Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardContent className="p-0">
                {documents && documents.length > 0 ? (
                  <div className="divide-y">
                    {documents.map((doc, idx) => (
                      <div key={idx} className="p-4 flex items-center justify-between hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-slate-500" />
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">
                              {doc.name || doc.document_type || "Document"}
                            </div>
                            <div className="text-sm text-slate-500">
                              {doc.created_at && new Date(doc.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.signed && (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Signed
                            </Badge>
                          )}
                          {doc.url && (
                            <Button variant="ghost" size="sm" asChild>
                              <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="font-medium text-slate-900 mb-1">No Documents</h3>
                    <p className="text-slate-500">No documents have been uploaded for this client.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Viewings Tab */}
          <TabsContent value="viewings">
            <Card>
              <CardContent className="p-0">
                {viewings && viewings.length > 0 ? (
                  <div className="divide-y">
                    {viewings.map((viewing, idx) => (
                      <div key={idx} className="p-4 hover:bg-slate-50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">
                              {viewing.viewing_date &&
                                new Date(viewing.viewing_date).toLocaleDateString("en-GB", {
                                  weekday: "short",
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })}
                            </span>
                            {viewing.viewing_time && (
                              <span className="text-slate-500">at {viewing.viewing_time}</span>
                            )}
                          </div>
                          <Badge
                            className={
                              viewing.feedback === "positive"
                                ? "bg-green-100 text-green-700"
                                : viewing.feedback === "negative"
                                ? "bg-red-100 text-red-700"
                                : "bg-slate-100 text-slate-700"
                            }
                          >
                            {viewing.feedback || viewing.status || "Scheduled"}
                          </Badge>
                        </div>
                        <div className="text-sm text-slate-600 ml-7">
                          <div>{viewing.property_address || viewing.address || "Property"}</div>
                          {viewing.viewer_name && (
                            <div className="text-slate-400">Viewer: {viewing.viewer_name}</div>
                          )}
                          {viewing.feedback_notes && (
                            <div className="mt-2 p-2 bg-slate-50 rounded text-slate-600">
                              <MessageSquare className="w-3 h-3 inline mr-1" />
                              {viewing.feedback_notes}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <Eye className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="font-medium text-slate-900 mb-1">No Viewings</h3>
                    <p className="text-slate-500">No viewings recorded for this client's properties.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Trail Tab */}
          <TabsContent value="audit">
            <Card>
              <CardContent className="p-0">
                {audit_trail && audit_trail.length > 0 ? (
                  <div className="divide-y">
                    {audit_trail.map((log, idx) => (
                      <div key={idx} className="p-4 hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">
                              {log.action?.replace(/_/g, " ") || "Activity"}
                            </div>
                            <div className="text-sm text-slate-500">
                              {log.created_at &&
                                new Date(log.created_at).toLocaleString("en-GB", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              {log.user_name && ` by ${log.user_name}`}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="font-medium text-slate-900 mb-1">No Activity</h3>
                    <p className="text-slate-500">No audit trail recorded for this client.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
